/**
 * Pure helpers for file-manager folders. The API returns a flat, sorted list of
 * `{ path, name, parent, file_count }` rows; files carry `folder_path` (null =
 * root). Everything here is string work, no I/O.
 */

/** "a/b/c" -> "a/b"; null for a top-level folder or the root. */
export const parentOf = (path) => {
  if (!path) return null;
  const pos = path.lastIndexOf("/");
  return pos === -1 ? null : path.slice(0, pos);
};

/** "a/b/c" -> "c" */
export const nameOf = (path) => {
  if (!path) return "";
  const pos = path.lastIndexOf("/");
  return pos === -1 ? path : path.slice(pos + 1);
};

/** joinPath("a/b", "c") -> "a/b/c"; joinPath(null, "c") -> "c" */
export const joinPath = (parent, name) => {
  const clean = String(name ?? "").trim().replace(/^\/+|\/+$/g, "");
  if (!clean) return parent ?? null;
  return parent ? `${parent}/${clean}` : clean;
};

/** True when `path` is `folder` itself or anything below it. */
export const isInside = (path, folder) => {
  if (!path || !folder) return false;
  return path === folder || path.startsWith(folder + "/");
};

/** Root and every ancestor of `path`, root first: "a/b/c" -> [null, "a", "a/b", "a/b/c"] */
export const breadcrumbOf = (path) => {
  const crumbs = [null];
  if (!path) return crumbs;
  let acc = "";
  for (const segment of path.split("/")) {
    acc = acc ? `${acc}/${segment}` : segment;
    crumbs.push(acc);
  }
  return crumbs;
};

/**
 * Nests the flat API list into a tree. Every node is
 * `{ path, name, file_count, children: [] }`, children sorted by name.
 * Parents the API did not send (should not happen, it fills them in) are created
 * on the fly so a child is never dropped.
 */
export const buildTree = (folders = []) => {
  const nodes = new Map();
  const ensure = (path) => {
    if (!nodes.has(path)) {
      nodes.set(path, { path, name: nameOf(path), file_count: 0, children: [] });
    }
    return nodes.get(path);
  };

  for (const folder of folders) {
    const node = ensure(folder.path);
    node.file_count = folder.file_count ?? 0;
  }

  const roots = [];
  for (const path of Array.from(nodes.keys())) {
    // parents first, so a synthesized parent exists before its child attaches
    const parent = parentOf(path);
    if (parent !== null) ensure(parent);
  }
  for (const node of nodes.values()) {
    const parent = parentOf(node.path);
    if (parent === null) roots.push(node);
    else nodes.get(parent).children.push(node);
  }

  const byName = (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  const sortDeep = (list) => {
    list.sort(byName);
    list.forEach((n) => sortDeep(n.children));
    return list;
  };
  return sortDeep(roots);
};

/** Files whose folder_path equals `folder` (null = root). */
export const filesInFolder = (files = [], folder = null) =>
  files.filter((file) => (file.folder_path ?? null) === (folder ?? null));
